export const decodeBase64ToFile = (base64String: string, fileName: string, mimeType: string) => {
  const byteString = atob(base64String);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
};

const mimeType = [
  { type: "image/jpeg", ext: "jpeg" }, 
  { type: "image/png", ext: "png" }, 
  { type: "image/jpg", ext: "jpg" },
  { type: "image/gif", ext: "gif" },
  { type: "image/svg+xml", ext: "svg" },
  { type: "application/pdf", ext: "pdf" },
  { type: "application/msword", ext: "doc" },
  { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", ext: "docx" },
  { type: "application/vnd.ms-excel", ext: "xls" },
  { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ext: "xlsx" },
  { type: "application/vnd.ms-powerpoint", ext: "ppt" },
  { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation", ext: "pptx" },
  { type: "text/plain", ext: "txt" },
  { type: "text/csv", ext: "csv" },
  { type: "application/json", ext: "json" },
  { type: "application/xml", ext: "xml" },
];

export const getMimeTypeFromFileName = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const mime = mimeType.find(m => m.ext === ext);
  return mime ? mime.type : 'application/octet-stream'; // Default to 'application/octet-stream' if not found
};

const buildStepFileObj = (encodedImage: string, imageLabel: string) => {
  if (!encodedImage) return null;
  const mime = getMimeTypeFromFileName(imageLabel);
  const file = decodeBase64ToFile(encodedImage, imageLabel, mime);
  return {
    name: file.name,
    type: file.type,
    size: file.size,
    lastModified: file.lastModified,
    webkitRelativePath: file.webkitRelativePath,
  };
};

const mapStepItem = (d: any, useCaseName?: string, useCaseId?: string) => {
  let images: any[] = [];
  if (d.Images) {
    if (Array.isArray(d.Images)) {
      images = d.Images;
    } else {
      try { images = JSON.parse(d.Images); } catch {}
    }
  }
  // Fallback for AIA: if Images array is empty but top-level ImageLink exists, build entry from step fields
  if (images.length === 0 && d.ImageLink) {
    images = [{
      Id: d.Id,
      Caption: d.ImageCaption || "",
      ImageLabel: d.ImageLabel || "",
      ImageLink: d.ImageLink,
    }];
  }
  return {
    step_id: d.Id,
    step_description: d.StepDescription,
    image_caption: d.ImageCaption,
    encoded_image: d.EncodedImage,
    image_label: d.ImageLabel,
    step_number: d.StepNumber,
    image_link: d.ImageLink,
    finding_id: d.FindingId,
    use_case_id: useCaseId ?? d.UseCaseId,
    use_case_name: useCaseName ?? null,
    images,
    file: buildStepFileObj(d.EncodedImage, d.ImageLabel),
  };
};

export const formatSteps = (data: any, isAiaTenant?: boolean) => {
  // Detect new AIA grouped use-case format: first item has a 'steps' array
  const isGroupedFormat =
    isAiaTenant && Array.isArray(data) && data.length > 0 && Array.isArray(data[0]?.steps);

  if (isGroupedFormat) {
    const allSteps: any[] = [];
    data.forEach((useCase: any) => {
      (useCase.steps || []).forEach((step: any) => {
        allSteps.push(mapStepItem(step, useCase.UseCaseName, useCase.UseCaseId));
      });
    });
    return allSteps;
  }

  // Old format / non-AIA — existing behaviour unchanged
  if (isAiaTenant) {
    return data.map((d: any) => mapStepItem(d));
  }

  return data.map((d: any) => {
    let fileObj: any = null;
    if (d.EncodedImage) {
      const mimeType = getMimeTypeFromFileName(d.ImageLabel);
      const file = decodeBase64ToFile(d.EncodedImage, d.ImageLabel, mimeType);
      fileObj = { name: file.name, type: file.type, size: file.size, lastModified: file.lastModified, webkitRelativePath: file.webkitRelativePath };
    } else if (d.ImageLabel || d.ImageLink) {
      // No base64 but has a stored image — create a placeholder so UI shows the file row
      const name = d.ImageLabel || (d.ImageLink ? d.ImageLink.split('/').pop()?.split('?')[0] || 'screenshot.png' : 'screenshot.png');
      fileObj = { name, type: getMimeTypeFromFileName(name), size: 0, lastModified: 0, webkitRelativePath: '' };
    }
    return {
      step_id: d.Id,
      step_description: d.StepDescription,
      image_caption: d.ImageCaption,
      encoded_image: d.EncodedImage,
      image_label: d.ImageLabel,
      step_number: d.StepNumber,
      image_link: d.ImageLink,
      finding_id: d.FindingId,
      use_case_id: null,
      use_case_name: null,
      images: [],
      file: fileObj,
    };
  });
};

const groupStepsIntoUseCases = (steps: any[]) => {
  const useCaseMap = new Map<string, any>();
  steps.forEach((step) => {
    const ucId = step.use_case_id;
    const ucKey = ucId ?? step.use_case_name ?? 'general';
    if (!ucId && !step.use_case_name) return;
    if (!useCaseMap.has(ucKey)) {
      useCaseMap.set(ucKey, {
        use_case_id: ucId,
        use_case_name: step.use_case_name || '',
        steps: [],
      });
    }
    useCaseMap.get(ucKey).steps.push(step);
  });
  return Array.from(useCaseMap.values());
};

export const formatReport = (data: any, isAiaTenant?: boolean) => {
  return {
    ...data,
    dateRange:[data.StartDate,data.EndDate],
    ReportName: data.ReportName,
    ClientName: data.ClientName,
    ApplicationName: data.ApplicationName,
    ReportId: data.Id || data.ReportId,
    findings:data?.FindingsList.map((d: any, index: any) => {
    const stepsData = formatSteps(d.StepsFollowedList, isAiaTenant);
    const isGroupedFormat = isAiaTenant && Array.isArray(d.StepsFollowedList) && d.StepsFollowedList.length > 0 && Array.isArray(d.StepsFollowedList[0]?.steps);
    const useCasesData = isAiaTenant
      ? (isGroupedFormat
          ? d.StepsFollowedList.map((uc: any) => ({
              use_case_id: uc.UseCaseId,
              use_case_name: uc.UseCaseName,
              steps: stepsData.filter((s: any) => s.use_case_id === uc.UseCaseId),
            }))
          : groupStepsIntoUseCases(stepsData))
      : undefined;
    return {
      ...d,
      finding_id: d.Id,
      finding: `Finding ${index + 1}`,
      vul_category: d.Category,
      vul_name: d.FindingName,
      vul_desc: d.Description,
      vul_assets_impacted: d.ImpactedAssets,
      vul_impact: d.Impact,
      vul_likelyhood_desc: d.LikelyhoodDescription,
      port: d.Port || "",
      cwe: d.CWE || "",
      steps: stepsData,
      useCases: useCasesData,
      isModified: false,
      aiGeneratedContent: {
        cvss_vector: d.CVSSVectorString,
        cvss_score: d.CVSSScore,
        cvss_severity: d.Severity,
        likelihood: d.Likelyhood,
        recommendation: d.Recommendation,
        priority: d.Priority,
        complexity: d.Complexity,
        references: d.ReferenceURLs,
        cvssValidation: false, // need to check,
        baseline_cvss_vector:  d.BaseLineCVSSVectorString
      },
      status: 'Completed',
      isLoaded: true,
    };
  })
}
};
